import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import { getEmployeesByDepartments } from "../utils/getEmployeesByDepartments";
import { getDepartments } from "../utils/getDepartments";
import { getAllEmployees } from "../utils/getAllEmployees";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { Users, Search, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, MoreVertical, X, CheckCircle, XCircle } from "lucide-react";
import Select, { components } from "react-select";
import { uploadFiles } from "../utils/uploadFiles";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatProjectTitle } from "../utils/formatProjectTitle";

export default function AccessManagement() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allAvailableEmployees, setAllAvailableEmployees] = useState([]); // All employees for dropdown
  const [savedDepartmentEmployeesForTable, setSavedDepartmentEmployeesForTable] = useState([]); // For Active Users table (saved state only)
  const [loadingSavedTableEmployees, setLoadingSavedTableEmployees] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [userItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userTableDepartmentFilter, setUserTableDepartmentFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showInactiveEmployees, setShowInactiveEmployees] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [updatingEmployeeId, setUpdatingEmployeeId] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [employeeSelectMenuOpen, setEmployeeSelectMenuOpen] = useState(false);
  
  // Theme: single source so Select control + menu stay in sync (no theme "flip" on click)
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const el = document.documentElement;
    const check = () => setIsDarkMode(el.classList.contains("dark"));
    const obs = new MutationObserver(check);
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  
  // Confirm dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Add Employee modal state
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [addEmployeeEmail, setAddEmployeeEmail] = useState("");
  const [addEmployeeSaving, setAddEmployeeSaving] = useState(false);
  const [addEmployeeError, setAddEmployeeError] = useState("");
  const [addModalEmployeeList, setAddModalEmployeeList] = useState([]); // directory employees for email autofill
  const [addModalOptionsLoading, setAddModalOptionsLoading] = useState(false);
  const [addEmployeeSuggestionsOpen, setAddEmployeeSuggestionsOpen] = useState(false);
  const addEmployeeSuggestionsRef = useRef(null);

  // Save access success popup
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Modal to search and manage selected employees (+N more)
  const [showSelectedEmployeesModal, setShowSelectedEmployeesModal] = useState(false);
  const [selectedEmployeesModalSearch, setSelectedEmployeesModalSearch] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
    videos: [],
    images: [],
    division: [],
    assignedUsers: [],
    status: "active",
    workspaceKey: "",
    firebaseProjectId: "",
    department: "", // NEW: Department field
  });

  const projectsRef = collection(db, "workspaces", "ai-apps-dashboard", "projects");
  const membersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");

  // Fetch data (projects, team members)
  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsSnap, membersSnap] = await Promise.all([
        getDocs(projectsRef),
        getDocs(membersRef),
      ]);

      let projectsData = projectsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const membersData = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Sort by sequence if available, otherwise by createdAt
      projectsData.sort((a, b) => {
        if (typeof a.sequence === 'number' && typeof b.sequence === 'number') {
          return a.sequence - b.sequence;
        }
        if (typeof a.sequence === 'number') return -1;
        if (typeof b.sequence === 'number') return 1;
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateA - dateB;
      });

      setProjects(projectsData);
      setTeamMembers(membersData);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch available departments
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const result = await getDepartments();
        if (result.ok) {
          setAvailableDepartments(result.departments || []);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch all employees for dropdown from BigQuery (directory_employees)
  useEffect(() => {
    const fetchAllEmployees = async () => {
      if (!projectId) return;

      setLoadingAllEmployees(true);
      try {
        const result = await getAllEmployees();
        if (result.ok && Array.isArray(result.employees)) {
          const employees = result.employees.map((emp) => ({
            id: emp.id || emp.email,
            email: emp.email || "",
            name: emp.name || "",
            fullName: emp.fullName || emp.name || "",
            department: (emp.department || "").trim(),
            ...emp,
          }));
          employees.sort((a, b) => {
            const nameA = (a.fullName || a.name || "").toLowerCase();
            const nameB = (b.fullName || b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setAllAvailableEmployees(employees);
        } else {
          setAllAvailableEmployees([]);
        }
      } catch (err) {
        console.error("Error fetching all employees:", err);
        setAllAvailableEmployees([]);
      } finally {
        setLoadingAllEmployees(false);
      }
    };

    if (projectId) {
      fetchAllEmployees();
    }
  }, [projectId]);

  // Initialize selected departments and employees from project (assignedUsers stored as emails; resolve legacy numeric IDs to email when possible)
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        // Set selected departments
        let depts = [];
        if (Array.isArray(project.department)) {
          depts = project.department;
        } else if (typeof project.department === 'string' && project.department.trim()) {
          const split = project.department.split(/[,;|\n]/).map(d => d.trim()).filter(d => d.length > 0);
          depts = split.length > 1 ? split : [project.department.trim()];
        }
        setSelectedDepartments(depts);

        // Set selected employees: use emails. Resolve legacy numeric IDs to email via teamMembers when possible.
        if (project.assignedUsers && Array.isArray(project.assignedUsers)) {
          const resolved = project.assignedUsers.map((x) => {
            const s = (x || "").toString().trim();
            if (!s) return null;
            if (s.includes("@")) return s.toLowerCase();
            const m = teamMembers.find((t) => t.id === s || (t.email || "").toLowerCase().trim() === s.toLowerCase());
            return m ? (m.email || "").toLowerCase().trim() : s;
          }).filter(Boolean);
          setSelectedEmployeeIds([...new Set(resolved)]);
        } else {
          setSelectedEmployeeIds([]);
        }
      }
    } else {
      setSelectedDepartments([]);
      setSelectedEmployeeIds([]);
    }
  }, [projectId, projects, teamMembers]);

  // Track previous departments to detect changes
  const prevDepartmentsRef = useRef([]);

  // Fetch saved project's department employees for Active Users table (table shows saved state only, not unsaved selection)
  useEffect(() => {
    const fetchSavedForTable = async () => {
      if (!projectId || !projects.length) {
        setSavedDepartmentEmployeesForTable([]);
        return;
      }
      const project = projects.find(p => p.id === projectId);
      const savedDepts = Array.isArray(project?.department) ? project.department : (project?.department ? [project.department] : []);
      if (savedDepts.length === 0) {
        setSavedDepartmentEmployeesForTable([]);
        return;
      }
      setLoadingSavedTableEmployees(true);
      try {
        const result = await getEmployeesByDepartments(savedDepts);
        if (result.ok && Array.isArray(result.employees)) {
          setSavedDepartmentEmployeesForTable(result.employees);
        } else {
          setSavedDepartmentEmployeesForTable([]);
        }
      } catch (err) {
        console.error("Error fetching saved department employees for table:", err);
        setSavedDepartmentEmployeesForTable([]);
      } finally {
        setLoadingSavedTableEmployees(false);
      }
    };
    fetchSavedForTable();
  }, [projectId, projects]);

  // Fetch employees when departments are selected; by default select all employees from selected departments
  useEffect(() => {
    const fetchAndSelectEmployees = async () => {
      if (selectedDepartments.length === 0) {
        setDepartmentEmployees([]);
        setAllEmployees([]);
        setSelectedEmployeeIds([]); // Remove all department employees when no department is selected
        prevDepartmentsRef.current = [];
        return;
      }

      setLoadingEmployees(true);
      try {
        const result = await getEmployeesByDepartments(selectedDepartments);
        if (result.ok) {
          const employees = result.employees || [];
          setDepartmentEmployees(employees);
          setAllEmployees(employees);

          // By default: when department(s) are selected, select ALL employees from those departments (store emails in assignedUsers)
          const emails = employees.map((emp) => (emp.email || "").toLowerCase().trim()).filter(Boolean);
          // Departments unchanged from the saved project (initial load): keep saved selection and
          // add new department joiners, but don't re-select users the admin excluded.
          const savedProject = projects.find((p) => p.id === projectId);
          const savedDepts = Array.isArray(savedProject?.department)
            ? savedProject.department
            : (savedProject?.department ? [savedProject.department] : []);
          const sameAsSaved =
            savedProject &&
            savedDepts.length === selectedDepartments.length &&
            savedDepts.every((d) => selectedDepartments.includes(d));
          if (sameAsSaved) {
            const excludedSet = new Set(
              (Array.isArray(savedProject.excludedUsers) ? savedProject.excludedUsers : [])
                .map((e) => (e || "").toString().toLowerCase().trim())
            );
            const saved = (Array.isArray(savedProject.assignedUsers) ? savedProject.assignedUsers : [])
              .map((e) => (e || "").toString().toLowerCase().trim())
              .filter(Boolean);
            setSelectedEmployeeIds([...new Set([...saved, ...emails.filter((e) => !excludedSet.has(e))])]);
          } else {
            setSelectedEmployeeIds(emails);
          }
          prevDepartmentsRef.current = [...selectedDepartments];
        } else {
          setDepartmentEmployees([]);
          setAllEmployees([]);
        }
      } catch (err) {
        console.error("Error fetching department employees:", err);
        setDepartmentEmployees([]);
        setAllEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    if (selectedDepartments.length > 0) {
      fetchAndSelectEmployees();
    } else {
      setDepartmentEmployees([]);
      setAllEmployees([]);
      setSelectedEmployeeIds([]); // Remove all employees when all departments are unselected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartments]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
      if (addEmployeeSuggestionsRef.current && !addEmployeeSuggestionsRef.current.contains(event.target)) {
        setAddEmployeeSuggestionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle dropdown menu
  const toggleMenu = (projectId) => {
    setOpenMenuId(openMenuId === projectId ? null : projectId);
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (project) => {
    setProjectToDelete(project);
    setShowConfirmDialog(true);
    setOpenMenuId(null);
  };

  // Soft Delete - Move to trash
  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      console.log("🗑️ Starting soft delete for:", projectToDelete.title);
      
      const trashRef = collection(db, "workspaces", "ai-apps-dashboard", "trash");
      const trashDoc = await addDoc(trashRef, {
        ...projectToDelete,
        deletedAt: serverTimestamp(),
        originalCollection: "projects",
        originalProjectId: projectToDelete.id,
      });
      
      console.log("✅ Project saved to trash with ID:", trashDoc.id);

      const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", projectToDelete.id);
      await deleteDoc(projectRef);
      
      console.log("✅ Project removed from active projects");
      
      setProjects((prevProjects) => prevProjects.filter((p) => p.id !== projectToDelete.id));
      
      setShowConfirmDialog(false);
      setProjectToDelete(null);
      
      console.log("✅ Soft delete completed successfully");
    } catch (err) {
      console.error("❌ Error moving project to trash:", err);
      setShowConfirmDialog(false);
      setProjectToDelete(null);
    }
  };

  // Add Employee modal - open and reset; load directory employees for email autofill
  const openAddEmployeeModal = async () => {
    setAddEmployeeEmail("");
    setAddEmployeeError("");
    setAddEmployeeSuggestionsOpen(false);
    setShowAddEmployeeModal(true);
    if (allAvailableEmployees.length > 0) {
      setAddModalEmployeeList(allAvailableEmployees);
      setAddModalOptionsLoading(false);
    } else {
      setAddModalOptionsLoading(true);
      try {
        const result = await getAllEmployees();
        if (result.ok && Array.isArray(result.employees)) {
          const employees = result.employees.map((emp) => ({
            id: emp.id || emp.email,
            email: (emp.email || "").trim(),
            name: emp.name || "",
            fullName: emp.fullName || emp.name || "",
            department: (emp.department || "").trim(),
          })).filter((e) => e.email);
          employees.sort((a, b) => {
            const nameA = (a.fullName || a.name || "").toLowerCase();
            const nameB = (b.fullName || b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setAddModalEmployeeList(employees);
        } else {
          setAddModalEmployeeList([]);
        }
      } catch (err) {
        console.error("Error loading employees for add modal:", err);
        setAddModalEmployeeList([]);
      } finally {
        setAddModalOptionsLoading(false);
      }
    }
  };

  // Add employee only to this project's assignedUsers (no devfolio-users or other collections)
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const email = addEmployeeEmail?.trim()?.toLowerCase();
    if (!email) {
      setAddEmployeeError("Please enter an email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAddEmployeeError("Please enter a valid email address.");
      return;
    }
    if (!projectId) {
      setAddEmployeeError("Please select a project first.");
      return;
    }
    const project = projects.find((p) => p.id === projectId);
    if (!project) {
      setAddEmployeeError("Project not found.");
      return;
    }
    setAddEmployeeError("");
    setAddEmployeeSaving(true);
    try {
      const currentAssigned = Array.isArray(project.assignedUsers) ? project.assignedUsers : [];
      if (currentAssigned.some((id) => (id || "").toLowerCase().trim() === email)) {
        setAddEmployeeError("This email is already assigned to this project.");
        setAddEmployeeSaving(false);
        return;
      }
      const currentInactive = Array.isArray(project.inactiveAssignedUsers) ? project.inactiveAssignedUsers : [];
      const nextInactive = currentInactive.filter((e) => (e || "").toLowerCase().trim() !== email);
      const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", projectId);
      await updateDoc(projectRef, {
        assignedUsers: [...currentAssigned, email],
        inactiveAssignedUsers: nextInactive,
        updatedAt: serverTimestamp(),
      });
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, assignedUsers: [...currentAssigned, email], inactiveAssignedUsers: nextInactive }
            : p
        )
      );
      setSelectedEmployeeIds((prev) => (prev.includes(email) ? prev : [...prev, email]));
      setShowAddEmployeeModal(false);
      setAddEmployeeEmail("");
    } catch (err) {
      console.error("Error adding employee:", err);
      setAddEmployeeError(err.message || "Failed to add employee. Please try again.");
    } finally {
      setAddEmployeeSaving(false);
    }
  };

  // Toggle active/inactive: save only to this project's inactiveAssignedUsers (no users or devfolio-users collections).
  const handleEmployeeToggle = async (emp) => {
    const email = (emp.email || "").toLowerCase().trim();
    if (!email) return;
    if (!projectId) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setUpdatingEmployeeId(emp.id || emp.email);
    try {
      const currentInactive = Array.isArray(project.inactiveAssignedUsers) ? project.inactiveAssignedUsers : [];
      const isCurrentlyInactive = currentInactive.some((e) => (e || "").toLowerCase().trim() === email);
      const nextInactive = isCurrentlyInactive
        ? currentInactive.filter((e) => (e || "").toLowerCase().trim() !== email)
        : [...currentInactive, email];

      const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", projectId);
      await updateDoc(projectRef, {
        inactiveAssignedUsers: nextInactive,
        updatedAt: serverTimestamp(),
      });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, inactiveAssignedUsers: nextInactive } : p))
      );
    } catch (err) {
      console.error("Error toggling employee status:", err);
    } finally {
      setUpdatingEmployeeId(null);
    }
  };

  // Filter projects based on search
  const filteredProjects = projects.filter((p) => {
    const q = search.toLowerCase();
    return (
      (p.title && p.title.toLowerCase().includes(q)) ||
      (p.description && p.description.toLowerCase().includes(q)) ||
      (p.division && p.division.some(d => d.toLowerCase().includes(q)))
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    setUserPage(1);
  }, [userSearch]);

  // Get users grouped by project with their departments (assignedUsers are stored as emails; support legacy ID for backward compat)
  const getUsersByProject = () => {
    const usersByProject = [];
    const norm = (s) => (s || "").toString().toLowerCase().trim();

    projects.forEach((project) => {
      if (project.assignedUsers && project.assignedUsers.length > 0 && project.department) {
        project.assignedUsers.forEach((idOrEmail) => {
          const member = teamMembers.find(
            (m) => m.id === idOrEmail || norm(m.email) === norm(idOrEmail)
          );
          if (member) {
            usersByProject.push({
              id: member.id,
              name: member.name || member.fullName || "Unnamed",
              email: member.email || "",
              department: project.department || "—",
              projectTitle: project.title || "—",
              projectId: project.id,
              isActive: member.isActive !== undefined ? member.isActive : true,
            });
          }
        });
      }
    });

    return usersByProject;
  };

  const allAssignedUsers = getUsersByProject();

  // Filter users based on search and active/inactive
  const filteredUsers = allAssignedUsers.filter((u) => {
    // Filter by active/inactive status
    if (showInactive) {
      if (u.isActive !== false) return false;
    } else {
      if (u.isActive === false) return false;
    }
    
    // Filter by search query (safe for string or object name)
    const q = userSearch.toLowerCase();
    const deptStr = typeof u.department === "string"
      ? u.department.toLowerCase()
      : Array.isArray(u.department)
        ? u.department.join(" ").toLowerCase()
        : "";
    const toStr = (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.toLowerCase();
      if (typeof val === "object" && val !== null) {
        if (typeof val.full === "string") return val.full.toLowerCase();
        if (typeof val.given === "string" || typeof val.family === "string") {
          return [val.given, val.family].filter(Boolean).join(" ").toLowerCase();
        }
      }
      return "";
    };
    const nameStr = toStr(u.name);
    const emailStr = typeof u.email === "string" ? u.email.toLowerCase() : "";
    const titleStr = typeof u.projectTitle === "string" ? u.projectTitle.toLowerCase() : "";
    return (
      (nameStr && nameStr.includes(q)) ||
      (emailStr && emailStr.includes(q)) ||
      (deptStr && deptStr.includes(q)) ||
      (titleStr && titleStr.includes(q))
    );
  });

  // User pagination
  const userTotalPages = Math.ceil(filteredUsers.length / userItemsPerPage);
  const userStartIndex = (userPage - 1) * userItemsPerPage;
  const userEndIndex = userStartIndex + userItemsPerPage;
  const currentUsers = filteredUsers.slice(userStartIndex, userEndIndex);

  const handleUserPageChange = (page) => {
    setUserPage(page);
  };

  const getUserPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (userTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= userTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (userPage <= 3) {
        pages.push(1, 2, 3, 4, '...', userTotalPages);
      } else if (userPage >= userTotalPages - 2) {
        pages.push(1, '...', userTotalPages - 3, userTotalPages - 2, userTotalPages - 1, userTotalPages);
      } else {
        pages.push(1, '...', userPage - 1, userPage, userPage + 1, '...', userTotalPages);
      }
    }
    
    return pages;
  };

  const inactiveUsersCount = allAssignedUsers.filter(u => u.isActive === false).length;

  // Form handlers
  const openForm = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        description: project.description || "",
        link: project.link || "",
        videos: project.videos || [],
        images: project.images || [],
        division: project.division || [],
        assignedUsers: project.assignedUsers || [],
        status: project.status || "active",
        workspaceKey: project.workspaceKey || "",
        firebaseProjectId: project.firebaseProjectId || "",
        department: project.department || "", // NEW: Load department
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: "",
        description: "",
        link: "",
        videos: [],
        images: [],
        division: [],
        assignedUsers: [],
        status: "active",
        workspaceKey: "",
        firebaseProjectId: "",
        department: "", // NEW: Empty for new projects
      });
    }
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({
      ...formData,
      [name]: name === "images" ? [...formData.images, ...files] : [...files],
    });
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const firebaseProjectId = (formData.firebaseProjectId || "").trim();

      let imageUrls = formData.images;
      let videoUrls = formData.videos;

      if (formData.images.some((f) => f instanceof File)) {
        imageUrls = await uploadFiles(
          formData.images.filter((f) => f instanceof File),
          "images"
        );
      }

      if (formData.videos.some((f) => f instanceof File)) {
        videoUrls = await uploadFiles(formData.videos, "videos");
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        link: formData.link,
        division: formData.division,
        assignedUsers: formData.assignedUsers,
        images: imageUrls,
        videos: videoUrls,
        status: formData.status,
        workspaceKey: formData.workspaceKey || "",
        firebaseProjectId,
        department: formData.department || "", // NEW: Save department
        updatedAt: new Date(),
      };

      if (editingProject) {
        const ref = doc(db, "workspaces", "ai-apps-dashboard", "projects", editingProject.id);
        await updateDoc(ref, { ...payload, updatedAt: serverTimestamp() });
        
        setProjects((prevProjects) =>
          prevProjects.map((p) =>
            p.id === editingProject.id ? { ...p, ...payload } : p
          )
        );
      } else {
        const maxSequence = projects.length > 0 
          ? Math.max(...projects.map(p => typeof p.sequence === 'number' ? p.sequence : -1)) 
          : -1;
        
        const newDocRef = await addDoc(projectsRef, {
          ...payload,
          sequence: maxSequence + 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        
        setProjects((prevProjects) => [
          ...prevProjects,
          { id: newDocRef.id, ...payload, sequence: maxSequence + 1, createdAt: new Date() }
        ]);
      }
      
      setShowForm(false);
    } catch (err) {
      console.error("❌ Error saving project:", err);
    } finally {
      setSaving(false);
    }
  };

  const divisionOptions = [
    { value: "Health", label: "Health" },
    { value: "System", label: "System" },
    { value: "Locums", label: "Locums" },
  ];

  const memberOptions = teamMembers.map((m) => ({
    value: m.id,
    label: `${m.name} (${m.role || "Member"})`,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh] text-gray-600 dark:text-gray-300">
        Loading access management...
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {projectId && (
            <button
              onClick={() => navigate('/admin/projects')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition flex items-center gap-2 text-gray-600 dark:text-gray-300"
              title="Go Back"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Access Management
            {projectId && projects.find(p => p.id === projectId) && (
              <span className="ml-2 text-lg text-indigo-500">
                - {projects.find(p => p.id === projectId)?.title}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddEmployeeModal}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 dark:bg-emerald-500 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 transition"
          >
            <Users size={18} /> Add Employee
          </button>
          {!projectId && (
            <button
              onClick={() => openForm()}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition"
            >
              <Plus size={18} /> Add Project
            </button>
          )}
        </div>
      </div>

      {/* Department and Employee Selection */}
      {projectId && (() => {
        const project = projects.find(p => p.id === projectId);
        if (!project) return null;

        // Prepare department options
        const departmentOptions = availableDepartments.map(dept => ({
          value: dept,
          label: dept
        }));

        // Employee options: use email as value so assignedUsers in Firestore stores emails (for filtering by logged-in user on Home)
        const employeeSource = selectedDepartments.length > 0 ? allEmployees : allAvailableEmployees;
        const employeeOptions = (employeeSource || []).map(emp => ({
          value: (emp.email || "").toLowerCase().trim() || emp.id,
          label: `${emp.fullName || emp.name || "Unnamed"} (${emp.email || ""})`,
          email: emp.email,
          name: emp.fullName || emp.name,
          department: emp.department || ""
        }));
        // Include selected IDs that might not be in current options (e.g. after unselecting department) so chips still show
        const allSelectedOptionsForModal = [
          ...employeeOptions.filter(opt => selectedEmployeeIds.includes(opt.value)),
          ...selectedEmployeeIds
            .filter(id => !employeeOptions.some(opt => opt.value === id))
            .map(id => ({ value: id, label: id, email: id, name: id, department: "" }))
        ];

        // Save function
        const handleSaveAccess = async () => {
          if (!projectId) return;
          
          setSaving(true);
          try {
            const project = projects.find((p) => p.id === projectId);
            const currentInactive = Array.isArray(project?.inactiveAssignedUsers) ? project.inactiveAssignedUsers : [];
            const assignedSet = new Set(selectedEmployeeIds.map((id) => (id || "").toLowerCase().trim()).filter(Boolean));
            const nextInactive = currentInactive.filter((e) => !assignedSet.has((e || "").toLowerCase().trim()));
            // Department employees the admin deselected; department-based visibility skips these.
            const excludedUsers = [...new Set(
              departmentEmployees
                .map((emp) => (emp.email || "").toLowerCase().trim())
                .filter((email) => email && !assignedSet.has(email))
            )];
            const projectRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", projectId);
            await updateDoc(projectRef, {
              department: selectedDepartments,
              assignedUsers: selectedEmployeeIds,
              excludedUsers,
              inactiveAssignedUsers: nextInactive,
              updatedAt: serverTimestamp()
            });

            // Refresh projects
            await fetchData();
            setShowSaveSuccess(true);
            setTimeout(() => setShowSaveSuccess(false), 2500);
          } catch (err) {
            console.error("Error saving access settings:", err);
            alert("Error saving access settings. Please try again.");
          } finally {
            setSaving(false);
          }
        };

        return (
          <div className="mt-4 bg-white dark:bg-gray-900 rounded-2xl shadow-md dark:shadow-none border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Select Department & Employees
            </h3>

            <div className="space-y-4">
              {/* Two Column Grid for Department and Employee Selectors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Department Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Department
                  </label>
                  <Select
                    key={`dept-${isDarkMode ? "dark" : "light"}`}
                    isMulti
                    isLoading={loadingDepartments}
                    options={departmentOptions}
                    value={departmentOptions.filter(opt => selectedDepartments.includes(opt.value))}
                    onChange={(selected) => {
                      const depts = selected ? selected.map(s => s.value) : [];
                      setSelectedDepartments(depts);
                    }}
                    isClearable
                    isSearchable
                    placeholder="Select Departments"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        borderColor: state.isFocused ? "#6366f1" : (isDarkMode ? "#4b5563" : "#d1d5db"),
                        borderRadius: "0.5rem",
                        minHeight: "44px",
                        boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
                        "&:hover": { borderColor: "#6366f1" },
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                        borderRadius: "0.5rem",
                        marginTop: "4px",
                        padding: "4px",
                        zIndex: 9999,
                        maxHeight: "300px",
                        boxShadow: isDarkMode ? "0 10px 15px -3px rgba(0,0,0,0.3)" : "0 10px 15px -3px rgba(0,0,0,0.1)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#4f46e5"
                          : state.isFocused
                            ? (isDarkMode ? "#374151" : "#e0e7ff")
                            : "transparent",
                        color: state.isSelected
                          ? "#ffffff"
                          : (isDarkMode ? "#f3f4f6" : "#111827"),
                        borderRadius: "0.375rem",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#6366f1",
                        borderRadius: "0.375rem",
                        padding: "0 4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#ffffff",
                        fontSize: "0.875rem",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#ffffff",
                        ":hover": {
                          backgroundColor: "#4f46e5",
                          color: "#ffffff",
                        },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#f3f4f6" : "#111827",
                      }),
                    }}
                  />
                  {selectedDepartments.length > 0 && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      All employees from selected departments will be automatically selected.
                    </p>
                  )}
                </div>

                {/* Employee Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Employees (Searchable)
                  </label>
                  {(() => {
                    const MAX_VISIBLE = 5;
                    const allSelectedOptions = employeeOptions.filter(opt => selectedEmployeeIds.includes(opt.value));
                    const remainingCount = allSelectedOptions.length - MAX_VISIBLE;
                    
                    return (
                      <div className="relative">
                        <Select
                          key={`emp-${isDarkMode ? "dark" : "light"}`}
                          isMulti
                          isLoading={loadingAllEmployees || loadingEmployees}
                          options={employeeOptions}
                          value={allSelectedOptions}
                          onChange={(selected) => {
                            const ids = selected ? selected.map(s => s.value) : [];
                            setSelectedEmployeeIds(ids);
                          }}
                          isClearable
                          isSearchable
                          placeholder="Search and select employees..."
                          className="basic-multi-select"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuIsOpen={employeeSelectMenuOpen}
                          onMenuOpen={() => setEmployeeSelectMenuOpen(true)}
                          onMenuClose={() => setEmployeeSelectMenuOpen(false)}
                          components={{
                            MultiValue: (props) => {
                              const index = allSelectedOptions.findIndex(opt => opt.value === props.data.value);
                              if (index >= MAX_VISIBLE) {
                                return null;
                              }
                              return <components.MultiValue {...props} />;
                            },
                          }}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                              borderColor: state.isFocused ? "#6366f1" : (isDarkMode ? "#4b5563" : "#d1d5db"),
                              borderRadius: "0.5rem",
                              minHeight: "44px",
                              boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
                              "&:hover": { borderColor: "#6366f1" },
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                              border: isDarkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                              borderRadius: "0.5rem",
                              marginTop: "4px",
                              padding: "4px",
                              zIndex: 9999,
                              maxHeight: "400px",
                              boxShadow: isDarkMode ? "0 10px 15px -3px rgba(0,0,0,0.3)" : "0 10px 15px -3px rgba(0,0,0,0.1)",
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? "#4f46e5"
                                : state.isFocused
                                  ? (isDarkMode ? "#374151" : "#e0e7ff")
                                  : "transparent",
                              color: state.isSelected
                                ? "#ffffff"
                                : (isDarkMode ? "#f3f4f6" : "#111827"),
                              borderRadius: "0.375rem",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "#6366f1",
                              borderRadius: "0.375rem",
                              padding: "0 4px",
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              color: "#ffffff",
                              fontSize: "0.875rem",
                            }),
                            multiValueRemove: (base) => ({
                              ...base,
                              color: "#ffffff",
                              ":hover": {
                                backgroundColor: "#4f46e5",
                                color: "#ffffff",
                              },
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: isDarkMode ? "#9ca3af" : "#6b7280",
                            }),
                            input: (base) => ({
                              ...base,
                              color: isDarkMode ? "#f3f4f6" : "#111827",
                            }),
                          }}
                        />
                        {remainingCount > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEmployeesModalSearch("");
                              setShowSelectedEmployeesModal(true);
                            }}
                            className="mt-2 inline-flex items-center bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition cursor-pointer shadow-sm"
                          >
                            +{remainingCount} more employee{remainingCount !== 1 ? "s" : ""} selected — search & manage
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {selectedEmployeeIds.length > 0 && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {selectedEmployeeIds.length} employee(s) selected.
                    </p>
                  )}
                </div>
              </div>

              {/* Modal: Search & manage selected employees */}
              <AnimatePresence>
                {showSelectedEmployeesModal && (
                  <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 dark:bg-black/60"
                    onClick={() => setShowSelectedEmployeesModal(false)}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Selected employees ({allSelectedOptionsForModal.length})
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowSelectedEmployeesModal(false)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                          aria-label="Close"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      <div className="px-5 pt-4 pb-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          <input
                            type="text"
                            value={selectedEmployeesModalSearch}
                            onChange={(e) => setSelectedEmployeesModalSearch(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0">
                        {(() => {
                          const q = (selectedEmployeesModalSearch || "").toLowerCase().trim();
                          const filtered = q
                            ? allSelectedOptionsForModal.filter(
                                (opt) =>
                                  (opt.label || "").toLowerCase().includes(q) ||
                                  (opt.email || "").toLowerCase().includes(q)
                              )
                            : allSelectedOptionsForModal;
                          if (filtered.length === 0) {
                            return (
                              <p className="py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                                {q ? "No selected employees match your search." : "No employees selected."}
                              </p>
                            );
                          }
                          return (
                            <ul className="space-y-1.5 py-2">
                              {filtered.map((opt) => (
                                <li
                                  key={opt.value}
                                  className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                                >
                                  <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                                    {opt.label}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedEmployeeIds((prev) => prev.filter((id) => id !== opt.value))
                                    }
                                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                                    title="Remove from selection"
                                  >
                                    <X size={16} />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          );
                        })()}
                      </div>
                      <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setShowSelectedEmployeesModal(false)}
                          className="px-4 py-2 rounded-xl bg-indigo-600 dark:bg-indigo-500 text-white font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition"
                        >
                          Done
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveAccess}
                  disabled={saving}
                  className="px-6 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? "Saving..." : "Save Access Settings"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Users Table - Show only assignedUsers for this project */}
      {(() => {
        // Filter projects - if projectId is provided, show only that project
        const filteredProjects = projectId 
          ? projects.filter(p => p.id === projectId)
          : projects;

        if (filteredProjects.length === 0) {
          return (
            <div className="mt-4 bg-white dark:bg-gray-900 rounded-2xl shadow-md dark:shadow-none border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {projectId 
                  ? "Project not found or no department selected."
                  : "No projects found. Add a project and select department in the edit modal."}
              </div>
            </div>
          );
        }

        const project = filteredProjects[0];
        const projectDept = Array.isArray(project.department) ? project.department[0] : (project.department || "");

        // Show only assignedUsers: one row per email in project.assignedUsers (no department/workspace/devfolio merge).
        // Also include current employees of the saved departments (new joiners), minus admin-excluded users.
        const excludedSet = new Set(
          (Array.isArray(project.excludedUsers) ? project.excludedUsers : [])
            .map((e) => (e || "").toString().toLowerCase().trim())
            .filter(Boolean)
        );
        const assignedUserIds = [...new Set([
          ...(Array.isArray(project.assignedUsers) ? project.assignedUsers : [])
            .map((x) => (x || "").toString().trim())
            .map((x) => (x.includes("@") ? x.toLowerCase() : x)),
          ...savedDepartmentEmployeesForTable
            .map((e) => (e.email || "").toString().toLowerCase().trim())
            .filter((email) => email && !excludedSet.has(email)),
        ])];
        const inactiveSet = new Set(
          (Array.isArray(project.inactiveAssignedUsers) ? project.inactiveAssignedUsers : [])
            .map((e) => (e || "").toLowerCase().trim())
            .filter(Boolean)
        );

        // Resolve display (name, department) from workspaces/ai-apps-dashboard/users first, then department employees, then email fallback
        const resolveDisplay = (idOrEmail) => {
          const normalized = (idOrEmail || "").toString().toLowerCase().trim();
          if (!normalized) return { id: idOrEmail, email: "", name: "", fullName: "", department: projectDept };
          const fromUsers = teamMembers.find((m) => (m.email || "").toLowerCase().trim() === normalized || m.id === idOrEmail);
          if (fromUsers) {
            const dept = (fromUsers.department && typeof fromUsers.department === "string") ? fromUsers.department.trim() : projectDept;
            return { id: fromUsers.id, email: fromUsers.email || normalized, name: fromUsers.name || "", fullName: fromUsers.fullName || fromUsers.name || "", department: dept };
          }
          const fromDept = savedDepartmentEmployeesForTable.find((e) => ((e.email || e.id) || "").toString().toLowerCase().trim() === normalized);
          if (fromDept) return { id: fromDept.id || fromDept.email, email: fromDept.email || normalized, name: fromDept.name || "", fullName: fromDept.fullName || fromDept.name || "", department: (fromDept.department && typeof fromDept.department === "string") ? fromDept.department : projectDept };
          const nameFromEmail = normalized.split("@")[0].replace(/[._]/g, " ") || "User";
          const displayName = nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
          return { id: normalized, email: normalized, name: displayName, fullName: displayName, department: projectDept };
        };

        const selectedEmployees = assignedUserIds
          .map((idOrEmail) => (idOrEmail || "").toString().trim())
          .filter(Boolean)
          .map((idOrEmail) => resolveDisplay(idOrEmail));

        // Active status only from project.inactiveAssignedUsers (in list = inactive)
        const employeesWithStatus = selectedEmployees.map((emp) => {
          const email = (emp.email || "").toLowerCase().trim();
          return { ...emp, _isActive: !inactiveSet.has(email) };
        });
        const activeEmployees = employeesWithStatus.filter((e) => e._isActive);
        const inactiveEmployees = employeesWithStatus.filter((e) => !e._isActive);
        const inactiveEmployeesCount = inactiveEmployees.length;

        // Filter by active/inactive list then by search
        const toSearchStr = (val) => {
          if (val == null) return "";
          if (typeof val === "string") return val.toLowerCase();
          if (typeof val === "object" && val !== null) {
            if (typeof val.full === "string") return val.full.toLowerCase();
            if (typeof val.given === "string" || typeof val.family === "string") {
              return [val.given, val.family].filter(Boolean).join(" ").toLowerCase();
            }
          }
          return "";
        };
        const listToFilter = showInactiveEmployees ? inactiveEmployees : activeEmployees;
        const uniqueDepartments = [...new Set(
          listToFilter
            .map((emp) => {
              const d = emp.department;
              if (typeof d === "string" && d.trim()) return d.trim();
              if (Array.isArray(d) && d.length) return d[0].trim();
              return null;
            })
            .filter(Boolean)
        )].sort();
        const filteredBySearch = listToFilter.filter((emp) => {
          const q = userSearch.toLowerCase();
          const deptStr = typeof emp.department === "string"
            ? emp.department.toLowerCase()
            : Array.isArray(emp.department)
              ? emp.department.join(" ").toLowerCase()
              : "";
          const nameStr = toSearchStr(emp.name);
          const fullNameStr = toSearchStr(emp.fullName);
          const emailStr = typeof emp.email === "string" ? emp.email.toLowerCase() : "";
          return (
            (fullNameStr && fullNameStr.includes(q)) ||
            (nameStr && nameStr.includes(q)) ||
            (emailStr && emailStr.includes(q)) ||
            (deptStr && deptStr.includes(q))
          );
        });
        const filteredEmployees = userTableDepartmentFilter
          ? filteredBySearch.filter((emp) => {
              const d = emp.department;
              const empDept = typeof d === "string" ? (d || "").trim() : (Array.isArray(d) && d[0] ? d[0].trim() : "");
              return empDept === userTableDepartmentFilter;
            })
          : filteredBySearch;

        // Pagination for employees
        const employeeTotalPages = Math.ceil(filteredEmployees.length / userItemsPerPage);
        const employeeStartIndex = (userPage - 1) * userItemsPerPage;
        const employeeEndIndex = employeeStartIndex + userItemsPerPage;
        const currentEmployees = filteredEmployees.slice(employeeStartIndex, employeeEndIndex);

        const getEmployeePageNumbers = () => {
          const pages = [];
          const maxVisiblePages = 5;
          
          if (employeeTotalPages <= maxVisiblePages) {
            for (let i = 1; i <= employeeTotalPages; i++) {
              pages.push(i);
            }
          } else {
            if (userPage <= 3) {
              pages.push(1, 2, 3, 4, '...', employeeTotalPages);
            } else if (userPage >= employeeTotalPages - 2) {
              pages.push(1, '...', employeeTotalPages - 3, employeeTotalPages - 2, employeeTotalPages - 1, employeeTotalPages);
            } else {
              pages.push(1, '...', userPage - 1, userPage, userPage + 1, '...', employeeTotalPages);
            }
          }
          
          return pages;
        };

        return (
          <div className="mt-4 bg-white dark:bg-gray-900 rounded-2xl shadow-md dark:shadow-none border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {showInactiveEmployees ? "Inactive Users" : "Active Users"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {projectId
                    ? "This project’s users + assigned to this project"
                    : "Users across projects"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowInactiveEmployees(false)}
                  className={`relative inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition ${
                    !showInactiveEmployees
                      ? "bg-indigo-600 dark:bg-indigo-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                  title="Active List"
                >
                  <CheckCircle size={18} className="flex-shrink-0" />
                  Active List
                </button>
                <button
                  type="button"
                  onClick={() => setShowInactiveEmployees(true)}
                  className={`relative inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition ${
                    showInactiveEmployees
                      ? "bg-indigo-600 dark:bg-indigo-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                  title="Inactive List"
                >
                  <XCircle size={18} className="flex-shrink-0" />
                  Inactive List
                  {inactiveEmployeesCount > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs rounded min-w-[20px] h-5 flex items-center justify-center px-1.5">
                      {inactiveEmployeesCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Search Bar + Department Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div className="flex items-center gap-2 min-w-[180px]">
                <label htmlFor="user-table-dept-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Department filter
                </label>
                <select
                  id="user-table-dept-filter"
                  value={userTableDepartmentFilter}
                  onChange={(e) => setUserTableDepartmentFilter(e.target.value)}
                  className="flex-1 py-2 pl-3 pr-8 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none dark:bg-gray-800 dark:text-gray-100 text-sm"
                >
                  <option value="">All departments</option>
                  {uniqueDepartments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            {loadingSavedTableEmployees ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Loading employees...
              </div>
            ) : (() => {
              const hasSavedDept = Array.isArray(project.department) ? project.department.length > 0 : (typeof project.department === "string" && (project.department || "").trim() !== "");
              return !hasSavedDept;
            })() ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Save access settings (assign users) above to see users in this table.
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {showInactiveEmployees
                  ? "No inactive employees or no match for your search."
                  : "No employees selected or found matching your search."}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {currentEmployees.map((emp, index) => {
                        const isActive = emp._isActive !== false;
                        const empKey = emp.id || emp.email || index;
                        const isUpdating = updatingEmployeeId === emp.id || updatingEmployeeId === emp.email;
                        return (
                          <tr
                            key={`emp-${empKey}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                          >
                            <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                              {emp.fullName || emp.name || "Unnamed"}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                              {emp.email || "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                              {emp.department || "—"}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  isActive
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                }`}
                              >
                                {isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => handleEmployeeToggle(emp)}
                                disabled={isUpdating}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 disabled:opacity-50 ${
                                  isActive
                                    ? "bg-indigo-600 dark:bg-indigo-500"
                                    : "bg-gray-300 dark:bg-gray-600"
                                }`}
                                title={isActive ? "Mark inactive" : "Mark active"}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    isActive ? "translate-x-6" : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}

                      {filteredEmployees.length === 0 && (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                          >
                            {loadingEmployees 
                              ? "Loading employees..." 
                              : selectedDepartments.length > 0
                                ? "No employees found matching your search."
                                : "Please select a department above."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {filteredEmployees.length > 0 && employeeTotalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      Showing {employeeStartIndex + 1} to {Math.min(employeeEndIndex, filteredEmployees.length)} of {filteredEmployees.length} users
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUserPageChange(userPage - 1)}
                        disabled={userPage === 1}
                        className={`p-2 rounded-lg border ${
                          userPage === 1
                            ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      <div className="flex items-center gap-1">
                        {getEmployeePageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="px-3 py-1.5 text-gray-500 dark:text-gray-400"
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handleUserPageChange(page)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                userPage === page
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>

                      <button
                        onClick={() => handleUserPageChange(userPage + 1)}
                        disabled={userPage === employeeTotalPages}
                        className={`p-2 rounded-lg border ${
                          userPage === employeeTotalPages
                            ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Add Employee Modal */}
      <AnimatePresence>
        {showAddEmployeeModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50"
            onClick={() => !addEmployeeSaving && setShowAddEmployeeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                  Add Employee
                </h2>
                <button
                  type="button"
                  onClick={() => !addEmployeeSaving && setShowAddEmployeeModal(false)}
                  disabled={addEmployeeSaving}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleAddEmployee} className="space-y-4">
                <div className="relative" ref={addEmployeeSuggestionsRef}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Employee Email
                  </label>
                  <input
                    type="email"
                    value={addEmployeeEmail}
                    onChange={(e) => {
                      setAddEmployeeEmail(e.target.value);
                      setAddEmployeeError("");
                      setAddEmployeeSuggestionsOpen(true);
                    }}
                    onFocus={() => setAddEmployeeSuggestionsOpen(true)}
                    placeholder="e.g. john.doe@company.com"
                    required
                    autoFocus
                    autoComplete="off"
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  {addModalOptionsLoading && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading directory...</p>
                  )}
                  {addEmployeeSuggestionsOpen && !addModalOptionsLoading && (() => {
                    const q = (addEmployeeEmail || "").toLowerCase().trim();
                    const filtered = q
                      ? addModalEmployeeList.filter(
                          (emp) =>
                            (emp.email || "").toLowerCase().includes(q) ||
                            (emp.fullName || "").toLowerCase().includes(q) ||
                            (emp.name || "").toLowerCase().includes(q)
                        )
                      : addModalEmployeeList;
                    const show = filtered.slice(0, 8);
                    if (show.length === 0) return null;
                    return (
                      <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg py-1">
                        {show.map((emp) => (
                          <li
                            key={emp.id || emp.email}
                            role="option"
                            className="px-3 py-2 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-gray-800 dark:text-gray-200 text-sm flex flex-col"
                            onClick={() => {
                              setAddEmployeeEmail(emp.email || "");
                              setAddEmployeeSuggestionsOpen(false);
                            }}
                          >
                            <span className="font-medium">{emp.email}</span>
                            {(emp.fullName || emp.name) && (
                              <span className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
                                {emp.fullName || emp.name}
                                {emp.department ? ` · ${emp.department}` : ""}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
                {addEmployeeError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{addEmployeeError}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => !addEmployeeSaving && setShowAddEmployeeModal(false)}
                    disabled={addEmployeeSaving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addEmployeeSaving}
                    className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
                  >
                    {addEmployeeSaving ? "Adding..." : "Add Employee"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add/Edit Project Modal */}
      <AnimatePresence>
        {showForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-5xl overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                  {editingProject ? "Edit Project" : "Add New Project"}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={20} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-6">
                {/* Row 1: Title & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="comingSoon">Coming Soon</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Division only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Division
                  </label>
                  <Select
                    isMulti
                    name="division"
                    options={divisionOptions}
                    value={divisionOptions.filter((opt) =>
                      (formData.division || []).includes(opt.value)
                    )}
                    onChange={(selected) =>
                      setFormData({
                        ...formData,
                        division: selected.map((s) => s.value),
                      })
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#1f2937"
                          : "#ffffff",
                        borderColor: state.isFocused ? "#6366f1" : "#4b5563",
                        borderRadius: "0.5rem",
                        minHeight: "44px",
                        boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "",
                        "&:hover": { borderColor: "#6366f1" },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#111827"
                          : "#ffffff",
                        borderRadius: "0.5rem",
                        marginTop: "4px",
                        padding: "4px",
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#4f46e5"
                          : state.isFocused
                            ? (document.documentElement.classList.contains("dark")
                              ? "#374151"
                              : "#e0e7ff")
                            : "transparent",
                        color: state.isSelected
                          ? "#ffffff"
                          : document.documentElement.classList.contains("dark")
                            ? "#f9fafb"
                            : "#111827",
                        borderRadius: "0.375rem",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#6366f1",
                        borderRadius: "0.375rem",
                        padding: "0 4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        fontSize: "0.875rem",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "#4f46e5",
                          color: "white",
                        },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: document.documentElement.classList.contains("dark")
                          ? "#9ca3af"
                          : "#6b7280",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: document.documentElement.classList.contains("dark")
                          ? "#f9fafb"
                          : "#111827",
                      }),
                      input: (base) => ({
                        ...base,
                        color: document.documentElement.classList.contains("dark")
                          ? "#f9fafb"
                          : "#111827",
                      }),
                    }}
                  />
                </div>

                {/* Description - Full Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Row 4: Project Link & Workspace Path */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Link
                    </label>
                    <input
                      type="url"
                      name="link"
                      value={formData.link}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Workspace Path <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="workspaceKey"
                      value={formData.workspaceKey}
                      onChange={handleChange}
                      placeholder="workspaces/smart-contact-tool/users OR pre-ra::workspaces/pre-ra-app/users"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Supports cross-project format: <code>projectId::workspaces/path/users</code>. If no
                      projectId is provided, default project is used.
                    </p>
                  </div>
                </div>

                {/* Row 5: External Firebase Project Config */}
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Firebase Project ID <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="firebaseProjectId"
                      value={formData.firebaseProjectId || ""}
                      onChange={handleChange}
                      placeholder="Example: pre-ra"
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                {/* Row 6: Images & Videos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Images
                    </label>
                    <input
                      type="file"
                      name="images"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {formData.images?.length > 0 && (
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        {formData.images.map((img, idx) => {
                          const src = img instanceof File ? URL.createObjectURL(img) : img;
                          return (
                            <div key={idx} className="relative">
                              <img
                                src={src}
                                alt=""
                                className="w-full h-24 object-cover rounded"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project Videos
                    </label>
                    <input
                      type="file"
                      name="videos"
                      accept="video/*"
                      multiple
                      onChange={handleFileChange}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border rounded-lg dark:border-gray-600 dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Project"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Save success popup */}
      <AnimatePresence>
        {showSaveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 dark:bg-emerald-500 text-white shadow-lg border border-emerald-700 dark:border-emerald-600"
          >
            <CheckCircle size={22} className="flex-shrink-0" />
            <span className="font-medium">Save successfully!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setProjectToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Project?"
        message={`Are you sure you want to move "${projectToDelete?.title}" to trash? You can restore it later from the trash.`}
        confirmText="Move to Trash"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
